import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { useState } from "react";
import { PublicHeader } from "./Landing";
import { Compass, Check, ArrowRight, CalendarClock, CalendarDays, Sparkles } from "lucide-react";

const MONTHLY_FEATURES = [
  "Full mentor chat", "Unlimited scan & measure", "Trends", "Guided paths & weekly steps",
  "Design & Build It", "Products & Services + AI listing sharpen", "Marketplace post packets",
  "Invoices & PDF download", "Saved sessions and pins",
];

export default function Pricing() {
  const nav = useNavigate();
  const { user, guest } = useAuth();
  const [loadingPlan, setLoadingPlan] = useState(null);

  const tryFree = async () => {
    if (user) { nav("/scan"); return; }
    try { await guest(); nav("/scan"); }
    catch { toast.error("Could not start a free preview"); }
  };

  const subscribe = async (lookup_key) => {
    if (!user) { nav("/login"); return; }
    if (user.paid) { nav("/home"); return; }
    setLoadingPlan(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setLoadingPlan(null); }
  };

  return (
    <div className="min-h-screen" style={{ background: "#FFFFFF", color: "var(--text)" }} data-testid="pricing-page">
      <PublicHeader />

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 pb-8 text-center">
        <h1 className="font-head font-extrabold tracking-tight text-4xl sm:text-5xl" data-testid="pricing-headline">Simple pricing. Full business stack.</h1>
        <p className="mt-4 text-base sm:text-lg max-w-2xl mx-auto" style={{ color: "var(--text-2)" }} data-testid="pricing-subhead">
          Everything you need to mentor an idea, design it, package it, and invoice — whether you're testing a $20 side hustle or a $2,500 service.
        </p>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-10">
        <div className="grid md:grid-cols-3 gap-5 items-start">
          {/* Guest */}
          <div className="pm-card p-6" data-testid="plan-guest">
            <div className="flex items-center gap-2"><Sparkles className="w-5 h-5" /><span className="font-head font-bold text-lg">Guest</span></div>
            <div className="mt-2 font-mono text-3xl font-bold">$0</div>
            <div className="label-mono">free preview</div>
            <ul className="mt-4 space-y-1.5 text-sm">
              <li className="flex items-center gap-2"><Check className="w-4 h-4" style={{ color: "var(--earn)" }} /> 1 free scan</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4" style={{ color: "var(--earn)" }} /> 1 free measure</li>
            </ul>
            <button onClick={tryFree} className="pm-btn-ghost w-full mt-5" data-testid="plan-guest-cta">Try free preview</button>
          </div>

          {/* Monthly */}
          <div className="pm-card p-6" data-testid="plan-monthly">
            <div className="flex items-center gap-2"><CalendarClock className="w-5 h-5" /><span className="font-head font-bold text-lg">Monthly</span></div>
            <div className="mt-2 font-mono text-3xl font-bold">$49<span className="text-base font-normal">/month</span></div>
            <div className="label-mono">billed monthly · cancel anytime</div>
            <ul className="mt-4 space-y-1.5 text-sm">
              {MONTHLY_FEATURES.map((f) => <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--earn)" }} /> {f}</li>)}
            </ul>
            <button onClick={() => subscribe("pm_monthly")} disabled={!!loadingPlan} className="pm-btn w-full mt-5" data-testid="plan-monthly-cta">
              {loadingPlan === "pm_monthly" ? "Redirecting…" : "Subscribe monthly"}
            </button>
          </div>

          {/* Yearly */}
          <div className="pm-card p-6 relative" style={{ borderColor: "var(--earn)" }} data-testid="plan-yearly">
            <span className="badge absolute -top-3 left-6" style={{ background: "var(--earn)", color: "#fff" }}>Best value</span>
            <div className="flex items-center gap-2"><CalendarDays className="w-5 h-5" /><span className="font-head font-bold text-lg">Yearly</span></div>
            <div className="mt-2 font-mono text-3xl font-bold">$490<span className="text-base font-normal">/year</span></div>
            <div className="label-mono">billed yearly · save about 2 months</div>
            <ul className="mt-4 space-y-1.5 text-sm">
              <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--earn)" }} /> Everything in Monthly</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--earn)" }} /> Save about 2 months vs monthly</li>
            </ul>
            <button onClick={() => subscribe("pm_yearly")} disabled={!!loadingPlan} className="pm-btn w-full mt-5" data-testid="plan-yearly-cta">
              {loadingPlan === "pm_yearly" ? "Redirecting…" : "Subscribe yearly"}
            </button>
          </div>
        </div>

        <p className="text-sm text-center mt-8" data-testid="pricing-code-note" style={{ color: "var(--text-2)" }}>
          Have an access code? <Link to="/login" className="underline">Redeem on the paywall.</Link>
        </p>
        <p className="text-xs text-center mt-3 max-w-2xl mx-auto" data-testid="pricing-fineprint" style={{ color: "var(--text-2)" }}>
          Path Mentor is an education and planning tool. It does not guarantee income. Cancel anytime on Monthly.
        </p>
      </section>

      <footer className="border-t mt-6" style={{ borderColor: "var(--border-subtle)" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 flex flex-wrap items-center gap-x-6 gap-y-2 label-mono">
          <button onClick={() => nav("/")} className="flex items-center gap-2"><Compass className="w-4 h-4" /> Path Mentor AI</button>
          <Link to="/" className="underline">Home</Link>
          <Link to="/login" className="underline">Sign in</Link>
          <a href="/privacy" className="underline">Privacy Policy</a>
          <a href="mailto:pathmentorai@gmail.com?subject=Path%20Mentor%20Support" className="underline">Contact support</a>
          <a href="tel:+18575203500" className="underline" data-testid="pricing-support-phone">(857) 520-3500</a>
        </div>
      </footer>
    </div>
  );
}
